import React, { useState } from "react";
import {
  Form,
  Input,
  Button,
  message,
  Space,
  Card,
  Checkbox,
  Spin,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import SimpleEditor from "./SimpleEditor";
import {
  _test_notification_email,
  _test_notification_sms,
  _test_notification_push,
  _test_notification_whatsapp,
  _test_notification_all,
} from "../../../common/axios_services";

const NotificationTestManager = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState([]);

  const channels = [
    { label: "Email", value: "email" },
    { label: "SMS", value: "sms" },
    { label: "Push", value: "push" },
    { label: "WhatsApp", value: "whatsapp" },
  ];

  const handleTest = async (values) => {
    if (selectedChannels.length === 0) {
      message.error("Please select at least one channel");
      return;
    }

    setLoading(true);
    try {
      const testData = {
        userId: values.userId || undefined,
        recipientEmail: values.recipientEmail || undefined,
        recipientPhoneNumber: values.recipientPhoneNumber || undefined,
        title: values.title,
        message: values.message,
      };

      if (selectedChannels.includes("email")) {
        await _test_notification_email(testData);
        message.success("Test email sent successfully");
      }
      if (selectedChannels.includes("sms")) {
        await _test_notification_sms(testData);
        message.success("Test SMS sent successfully");
      }
      if (selectedChannels.includes("push")) {
        await _test_notification_push(testData);
        message.success("Test push notification sent successfully");
      }
      if (selectedChannels.includes("whatsapp")) {
        await _test_notification_whatsapp(testData);
        message.success("Test WhatsApp sent successfully");
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to send test notification",
      );
    } finally {
      setLoading(false);
    }
  };

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  return (
    <Spin spinning={loading} indicator={antIcon}>
      <div className="space-y-6">
        <Card>
          <h3 className="text-lg font-bold mb-4">Test Notifications</h3>
          <p className="text-gray-600 mb-6">
            Send test notifications to verify channels are working correctly
          </p>

          <Form form={form} layout="vertical" onFinish={handleTest}>
            <Form.Item
              name="title"
              label="Title"
              rules={[{ required: true, message: "Title is required" }]}
            >
              <Input placeholder="Test Title" />
            </Form.Item>

            <Form.Item
              name="message"
              label="Message"
              rules={[{ required: true, message: "Message is required" }]}
            >
              <SimpleEditor
                value={form.getFieldValue("message") || ""}
                onChange={(val) => form.setFieldValue("message", val)}
                placeholder="Test message content"
              />
            </Form.Item>

            <Form.Item
              name="userId"
              label="User ID (Optional)"
            >
              <Input placeholder="user-id-here" />
            </Form.Item>

            <Form.Item
              name="recipientEmail"
              label="Recipient Email (Optional)"
            >
              <Input type="email" placeholder="test@example.com" />
            </Form.Item>

            <Form.Item
              name="recipientPhoneNumber"
              label="Recipient Phone Number (Optional)"
            >
              <Input placeholder="+234812345678" />
            </Form.Item>

            <Form.Item label="Channels">
              <div className="space-y-2 border p-4 rounded">
                {channels.map((channel) => (
                  <label
                    key={channel.value}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedChannels.includes(channel.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedChannels([
                            ...selectedChannels,
                            channel.value,
                          ]);
                        } else {
                          setSelectedChannels(
                            selectedChannels.filter(
                              (ch) => ch !== channel.value,
                            ),
                          );
                        }
                      }}
                    />
                    <span className="text-sm">{channel.label}</span>
                  </label>
                ))}
              </div>
            </Form.Item>

            <Space className="w-full justify-between">
              <Button onClick={() => form.resetFields()}>Reset</Button>
              <Button type="primary" htmlType="submit" size="large">
                Send Test Notification
              </Button>
            </Space>
          </Form>
        </Card>
      </div>
    </Spin>
  );
};

export default NotificationTestManager;
